import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Ico, COLORS } from './icons';
import { Page } from './page';
import { PageList, StructureSettings } from './panels';
import { UnsavedModal, ConflictModal, ExportModal, useFocusTrap } from './modals';
import { AutoInput } from './auto_input';
import { showToast } from '../lib/toast';
import { API } from '../lib/api';
import { exportPdf } from '../lib/export_pdf';
import { parseTags, serializeTags } from '../lib/tags';
import { TiptapToolbar } from './tiptap_editor';

function makeBlankDoc() {
  const tid = 't'+Math.random().toString(36).slice(2,7);
  const pid = 'p'+Math.random().toString(36).slice(2,7);
  return {
    name: 'Untitled document',
    tabs: [{ id:tid, label:'New page', color:'blue', location:'left', pageIds:[pid] }],
    pages: { [pid]: { id:pid, tabId:tid,
             size:'a4', layout:'single', tabSize:'small', content:null } }
  };
}

function PublishModal({ docName, onClose, onConfirm }) {
  const modalRef = useRef(null);
  useFocusTrap(modalRef);
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);
  return (
    <div className="modal-backdrop" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="publish-modal-title">
      <div className="modal modal--sm" onClick={e=>e.stopPropagation()} ref={modalRef}>
        <div className="modal__header">
          <h2 className="modal__title" id="publish-modal-title">Export "{docName}"?</h2>
          <button className="btn btn--icon" onClick={onClose} aria-label="Close dialog"><Ico.Close width="16" height="16"/></button>
        </div>
        <div className="modal__body">
          <p>Exporting will also publish this document to the community, making it visible to all pilots. Future saves will update the published version.</p>
        </div>
        <div className="modal__footer">
          <button className="btn btn--ghost btn--md" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary btn--md" onClick={onConfirm}><Ico.Download width="14" height="14"/> Publish & export</button>
        </div>
      </div>
    </div>
  );
}

function DeleteDocModal({ docName, onClose, onConfirm }) {
  const modalRef = useRef(null);
  useFocusTrap(modalRef);
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);
  return (
    <div className="modal-backdrop" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="delete-modal-title">
      <div className="modal modal--sm" onClick={e=>e.stopPropagation()} ref={modalRef}>
        <div className="modal__header">
          <h2 className="modal__title" id="delete-modal-title">Delete "{docName}"?</h2>
          <button className="btn btn--icon" onClick={onClose} aria-label="Close dialog"><Ico.Close width="16" height="16"/></button>
        </div>
        <div className="modal__body">
          <p className="confirm-public">This document will be permanently deleted. This action cannot be undone.</p>
        </div>
        <div className="modal__footer">
          <button className="btn btn--ghost btn--md" onClick={onClose}>Cancel</button>
          <button className="btn btn--danger btn--md" onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  );
}

function DocInfoPopover({ doc, tags, onUpdateDoc, onUpdateTags, onClose }) {
  const [tagInput, setTagInput] = useState('');
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const [allTags, setAllTags] = useState([]);
  const inputRef = useRef(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    fetch(API.tagsPath, { headers: { 'Accept': 'application/json' } })
      .then(r => {
        if (!r.ok) {
          const e = new Error(`HTTP ${r.status} loading tags`);
          e.status = r.status;
          throw e;
        }
        return r.json();
      })
      .then(data => setAllTags((data.tags || []).map(t => t.tag)))
      .catch(err => {
        console.error('tag fetch failed:', err);
        setAllTags([]);
      });
  }, []);

  const suggestions = useMemo(() => {
    const q = tagInput.toLowerCase().trim();
    const existingSet = new Set(tags.map(t => t.toLowerCase()));
    return allTags.filter(t =>
      !existingSet.has(t.toLowerCase()) && (!q || t.toLowerCase().includes(q))
    );
  }, [tagInput, tags, allTags]);

  const addTag = (tag) => {
    const cleaned = tag.replace(/[,.]/g, '').trim();
    if (!cleaned || tags.some(t => t.toLowerCase() === cleaned.toLowerCase())) return;
    onUpdateTags([...tags, cleaned]);
    setTagInput('');
    setHighlightIdx(-1);
  };

  const removeTag = (idx) => {
    onUpdateTags(tags.filter((_, i) => i !== idx));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightIdx(i => Math.min(i + 1, suggestions.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightIdx(i => Math.max(i - 1, -1)); return; }
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (highlightIdx >= 0 && highlightIdx < suggestions.length) addTag(suggestions[highlightIdx]);
      else if (tagInput.trim()) addTag(tagInput);
      return;
    }
    if (e.key === 'Backspace' && !tagInput && tags.length) removeTag(tags.length - 1);
  };

  useEffect(() => {
    const handler = (e) => {
      if (e.target.closest('.di-popover') || e.target.closest('.bb-title-pill') || e.target.closest('.di-backdrop')) return;
      onClose();
    };
    document.addEventListener('mousedown', handler, true);
    document.addEventListener('touchstart', handler, true);
    return () => { document.removeEventListener('mousedown', handler, true); document.removeEventListener('touchstart', handler, true); };
  }, [onClose]);

  useEffect(() => { inputRef.current?.focus(); }, []);

  return (
    <div className="di-popover" ref={popoverRef} onClick={e => e.stopPropagation()}>
      <div className="di-field">
        <label className="di-label">Title</label>
        <input className="input input--sm" value={doc.name} onChange={e => onUpdateDoc({ name: e.target.value })} placeholder="Untitled document"/>
      </div>
      <div className="di-field">
        <label className="di-label">Description</label>
        <textarea className="textarea" value={doc.description || ''} onChange={e => onUpdateDoc({ description: e.target.value })} placeholder="What's this document about?" rows={3}/>
      </div>
      <div className="di-field">
        <label className="di-label">Tags</label>
        <div className="di-tags-wrap">
          {tags.map((tag, i) => (
            <span key={i} className={`chip chip--tag`}>
              {tag}
              <button className="chip__remove" aria-label={`Remove tag ${tag}`} onClick={() => removeTag(i)}>×</button>
            </span>
          ))}
          <input ref={inputRef} className="di-tag-input" value={tagInput}
            onChange={e => { setTagInput(e.target.value); setHighlightIdx(-1); }}
            onKeyDown={handleKeyDown}
            placeholder={tags.length ? '' : 'Add tags…'}/>
        </div>
        {suggestions.length > 0 && (
          <div className="menu di-suggest">
            {suggestions.map((item, i) => (
              <button key={item}
                className={`menu__item${highlightIdx === i ? ' on' : ''}`}
                onMouseDown={e => e.preventDefault()}
                onClick={() => addTag(item)}>
                <span className="menu__dot menu__dot--tag"/>
                {item}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function AppEditor({ document: serverDoc, documentUrl, readOnly = false }) {
  const [doc, setDocRaw] = useState(() => {
    const content = serverDoc.content || {};
    const hasTabs = content.tabs && content.tabs.length > 0;
    const tags = parseTags(serverDoc.tags_string);
    if (hasTabs) {
      return { name: serverDoc.name, description: serverDoc.description, tags, ...content };
    }
    const blank = makeBlankDoc();
    return { ...blank, name: serverDoc.name, description: serverDoc.description, tags };
  });
  const [docDirty, setDocDirty] = useState(false);
  const docDirtyRef = useRef(false);
  const stableStringify = useCallback((obj) => JSON.stringify(obj, (_, v) =>
    v && typeof v === 'object' && !Array.isArray(v)
      ? Object.keys(v).sort().reduce((o, k) => { o[k] = v[k]; return o; }, {})
      : v
  ), []);
  const savedDocRef = useRef(null);
  // Revision of the server copy this editor is based on. Sent with every save
  // so the server can refuse to overwrite changes made elsewhere (e.g. by an
  // AI agent through the agent API) with this tab's stale full-content save.
  const revisionRef = useRef(serverDoc?.revision ?? null);
  savedDocRef.current ??= stableStringify(doc);
  const allowNavRef = useRef(false);
  const docRef = useRef(doc);
  docRef.current = doc;
  const historyRef = useRef({ past: [], future: [] });
  const [historyVersion, setHistoryVersion] = useState(0);
  const bumpHistory = useCallback(() => setHistoryVersion(v => v + 1), []);
  const checkDirty = useCallback((nextDoc) => {
    const dirty = stableStringify(nextDoc) !== savedDocRef.current;
    setDocDirty(dirty); docDirtyRef.current = dirty;
  }, []);
  const pushUndo = (snapshot) => {
    historyRef.current.past.push(snapshot);
    if (historyRef.current.past.length > 50) historyRef.current.past.shift();
  };

  // --- Editor content: transaction-driven grouping (500ms, matches ProseMirror) ---
  const lastEditorTxTime = useRef(0);
  const preEditorGroup = useRef(null);
  const pendingInsertSnapshot = useRef(null);
  const EDITOR_GROUP_DELAY = 500;

  const flushEditorGroup = () => {
    if (preEditorGroup.current !== null) {
      pushUndo(preEditorGroup.current);
      preEditorGroup.current = null;
    }
  };

  const setDocFromEditor = useCallback((v) => {
    if (pendingInsertSnapshot.current !== null) {
      flushEditorGroup();
      flushStructuralGroup();
      preEditorGroup.current = pendingInsertSnapshot.current;
      pendingInsertSnapshot.current = null;
      lastEditorTxTime.current = Date.now();
    } else {
      flushStructuralGroup();
      const now = Date.now();
      if (now - lastEditorTxTime.current > EDITOR_GROUP_DELAY) {
        flushEditorGroup();
        preEditorGroup.current = docRef.current;
      }
      lastEditorTxTime.current = now;
    }
    historyRef.current.future = [];
    const next = typeof v === 'function' ? v(docRef.current) : v;
    setDocRaw(next);
    docRef.current = next;
    checkDirty(next);
    bumpHistory();
  }, []);

  // --- Structural changes: 250ms idle timer (tabs, page settings, etc.) ---
  const idleTimer = useRef(null);
  const preStructuralGroup = useRef(null);

  const flushStructuralGroup = () => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    if (preStructuralGroup.current !== null) {
      pushUndo(preStructuralGroup.current);
      preStructuralGroup.current = null;
    }
  };

  const setDoc = useCallback((v) => {
    flushEditorGroup();
    if (preStructuralGroup.current === null) preStructuralGroup.current = docRef.current;
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => {
      flushStructuralGroup();
      bumpHistory();
    }, 250);
    historyRef.current.future = [];
    const next = typeof v === 'function' ? v(docRef.current) : v;
    setDocRaw(next);
    docRef.current = next;
    checkDirty(next);
    bumpHistory();
  }, []);

  // --- No-history updates (document title, description, tags) ---
  const setDocMeta = useCallback((v) => {
    const next = typeof v === 'function' ? v(docRef.current) : v;
    setDocRaw(next);
    docRef.current = next;
    checkDirty(next);
  }, []);

  // --- Undo / Redo ---
  const canUndo = historyRef.current.past.length > 0
    || preEditorGroup.current !== null
    || preStructuralGroup.current !== null;
  const canRedo = historyRef.current.future.length > 0;

  const undo = useCallback(() => {
    flushEditorGroup();
    flushStructuralGroup();
    const { past, future } = historyRef.current;
    if (!past.length) return;
    future.push(docRef.current);
    const snapshot = past.pop();
    setDocRaw(snapshot);
    docRef.current = snapshot;
    checkDirty(snapshot);
    bumpHistory();
  }, []);
  const redo = useCallback(() => {
    flushEditorGroup();
    flushStructuralGroup();
    const { past, future } = historyRef.current;
    if (!future.length) return;
    past.push(docRef.current);
    const snapshot = future.pop();
    setDocRaw(snapshot);
    docRef.current = snapshot;
    checkDirty(snapshot);
    bumpHistory();
  }, []);
  // Export/preview page order: outermost rail first, then inward; sides go
  // left, top, right, bottom. The side containers use reversed flex direction
  // (.side-left is row-reverse, .side-top is column-reverse), so rail 0 is
  // always the INNERMOST rail and the highest rail index is the outermost
  // (visually leftmost on the left side, topmost on the top side, etc.).
  // Reading-order-first export = outermost-first = sort rails DESCENDING.
  // Within a rail, insertion order = top-to-bottom / left-to-right visual
  // order, so preserve it. Puppeteer paginates in DOM order, so this drives
  // the PDF page sequence.
  const LOC_PRIORITY = { left: 0, top: 1, right: 2, bottom: 3 };
  const orderedTabs = useMemo(() => {
    return doc.tabs
      .map((t, idx) => ({ t, idx }))
      .sort((a, b) => {
        const la = LOC_PRIORITY[a.t.location || 'left'] ?? 0;
        const lb = LOC_PRIORITY[b.t.location || 'left'] ?? 0;
        if (la !== lb) return la - lb;
        const ra = a.t.rail || 0;
        const rb = b.t.rail || 0;
        if (ra !== rb) return rb - ra;
        return a.idx - b.idx;
      })
      .map(({ t }) => t);
  }, [doc.tabs]);
  const firstPageId = orderedTabs[0]?.pageIds[0] || Object.keys(doc.pages)[0];
  const [currentPageId, setCurrentPageId] = useState(firstPageId);
  const [mode, setMode] = useState(readOnly ? 'preview' : 'edit');
  const [selectedTabId, setSelectedTabId] = useState(doc.tabs[0]?.id);
  const [exportable, setExportable] = useState(() => !!serverDoc?.exportable);
  const [exporting, setExporting] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [showPublish, setShowPublish] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showDeleteDoc, setShowDeleteDoc] = useState(false);
  const [showUnsaved, setShowUnsaved] = useState(false);
  const [conflict, setConflict] = useState(null);
  const pendingNavRef = useRef(null);
  const [showDocMenu, setShowDocMenu] = useState(false);
  const [showDocInfo, setShowDocInfo] = useState(false);
  const [dragTabId, setDragTabId] = useState(null);
  const [uiMinimized, setUiMinimized] = useState(false);
  const [responsiveMode, setResponsiveMode] = useState(() => window.matchMedia('(max-width: 1230px)').matches);
  const [drawerOpen, setDrawerOpen] = useState(null);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1230px)');
    const handler = (e) => {
      setResponsiveMode(e.matches);
      if (e.matches) setDrawerOpen(null);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  const [zoom, setZoom] = useState(100);
  const workspaceRef = useRef(null);
  const [fitZoom, setFitZoom] = useState(100);
  const [activeEditor, setActiveEditor] = useState(null);
  const [, setEditorTick] = useState(0);
  const handleEditorReady = useCallback((ed) => {
    setActiveEditor(prev => {
      if (prev && prev !== ed) flushEditorGroup();
      return ed;
    });
  }, []);

  useEffect(() => {
    if (!activeEditor) return;
    const bump = () => setEditorTick(t => t + 1);
    activeEditor.on('selectionUpdate', bump);
    activeEditor.on('transaction', bump);
    return () => {
      activeEditor.off('selectionUpdate', bump);
      activeEditor.off('transaction', bump);
    };
  }, [activeEditor]);

  useEffect(() => {
    const el = workspaceRef.current;
    if (!el) return;
    const compute = () => {
      const padding = 64;
      const pageW = doc.orientation === 'landscape' ? 1123 : 794;
      const available = el.clientWidth - padding;
      setFitZoom(Math.max(25, Math.min(200, Math.floor((available / pageW) * 100))));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [doc.orientation]);

  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      const key = e.key.toLowerCase();
      if ((e.ctrlKey || e.metaKey) && key === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo(); else undo();
      }
      if ((e.ctrlKey || e.metaKey) && key === 'y') {
        e.preventDefault();
        redo();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [undo, redo]);

  const page = doc.pages[currentPageId] || Object.values(doc.pages)[0];

  useEffect(()=>{
    if (page && page.tabId) setSelectedTabId(page.tabId);
  }, [currentPageId]);

  // Deferred so the sweep batches across the transient window where
  // addInlineTab has added the tab but the followup insertPageButton hasn't
  // committed the button into doc content yet — React can split those into
  // two commits and a synchronous sweep would treat the new tab as orphan.
  const firstSweepDoneRef = useRef(false);
  useEffect(() => {
    const id = setTimeout(() => {
      const inlineTabs = doc.tabs.filter(t => t.inline);
      if (inlineTabs.length === 0) { firstSweepDoneRef.current = true; return; }

      const tabById = new Map(doc.tabs.map(t => [t.id, t]));
      const reachable = new Set();
      const queue = doc.tabs.filter(t => !t.inline).map(t => t.id);
      while (queue.length) {
        const tid = queue.shift();
        if (reachable.has(tid)) continue;
        reachable.add(tid);
        const tab = tabById.get(tid);
        if (!tab) continue;
        tab.pageIds.forEach(pid => {
          const pg = doc.pages[pid];
          if (!pg) return;
          const walk = (node) => {
            if (!node) return;
            if (node.type === 'pageButton' && node.attrs?.tabId) queue.push(node.attrs.tabId);
            (node.content || []).forEach(walk);
          };
          if (pg.content) walk(pg.content);
          if (pg.content2) walk(pg.content2);
        });
      }

      const orphans = inlineTabs.filter(t => !reachable.has(t.id));
      if (orphans.length === 0) { firstSweepDoneRef.current = true; return; }

      const orphanSet = new Set(orphans.map(t => t.id));
      const wasFirstSweep = !firstSweepDoneRef.current;
      firstSweepDoneRef.current = true;
      setDocMeta(d => {
        const newPages = { ...d.pages };
        d.tabs.filter(t => orphanSet.has(t.id)).forEach(t =>
          t.pageIds.forEach(pid => { delete newPages[pid]; })
        );
        return { ...d, tabs: d.tabs.filter(t => !orphanSet.has(t.id)), pages: newPages };
      });
      if (wasFirstSweep) {
        // Server-side orphan reaped on mount: re-baseline so the doc reads
        // clean. Without this, opening a doc with stale orphan inline data
        // would flip docDirty before the user has touched anything.
        savedDocRef.current = stableStringify(docRef.current);
        setDocDirty(false); docDirtyRef.current = false;
      }
    }, 300);
    return () => clearTimeout(id);
  }, [doc]);

  useEffect(() => {
    if (mode === 'preview') {
      document.querySelectorAll('.page-content').forEach(el => { el.scrollTop = 0; });
    }
  }, [mode]);

  useEffect(() => {
    const handler = (e) => {
      if (!docDirtyRef.current || allowNavRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  const guardNavRef = useRef(null);
  guardNavRef.current = (href, formEl) => {
    if (!docDirtyRef.current) return false;
    pendingNavRef.current = formEl || href;
    setShowUnsaved(true);
    return true;
  };

  useEffect(() => {
    const brandbar = document.getElementById('brandbar');
    if (!brandbar) return;

    const handleClick = (e) => {
      const link = e.target.closest('a[href]');
      if (link && link.closest('#brandbar')) {
        const href = link.getAttribute('href');
        if (guardNavRef.current(href)) { e.preventDefault(); e.stopImmediatePropagation(); }
        return;
      }
      const btn = e.target.closest('button[type="submit"], .menu__item--danger');
      if (btn) {
        const form = btn.closest('form');
        if (form && guardNavRef.current(null, form)) { e.preventDefault(); e.stopImmediatePropagation(); }
      }
    };
    brandbar.addEventListener('click', handleClick, true);
    return () => brandbar.removeEventListener('click', handleClick, true);
  }, []);

  const toast = useCallback((text, opts) => showToast(text, opts), []);

  // Saves run one at a time: each request sends the revision returned by the
  // previous one, so a quick double save (Ctrl-S twice) can't 409 against
  // itself. Each save sends the doc as it is when the request starts and only
  // marks *that* snapshot as saved, so edits typed during a save stay dirty.
  const saveChainRef = useRef(Promise.resolve());
  const performSave = useCallback(async (extras) => {
    if (!documentUrl) return false;
    const snapshot = docRef.current;
    const { name, description, tags, tabs, pages, ...rest } = snapshot;
    const body = { base_revision: revisionRef.current, document: { name, description, tags_string: serializeTags(tags || []), content: { tabs, pages, ...rest }, ...extras } };
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
    try {
      const r = await fetch(documentUrl, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', ...(csrf && { 'X-CSRF-Token': csrf }) },
        body: JSON.stringify(body)
      });
      const data = await r.json().catch(() => ({}));
      if (r.status === 409) {
        setConflict({ revision: data.revision });
        return false;
      }
      if (!r.ok) {
        console.error('Failed to save document:', r.status, data);
        toast(data.errors?.[0] || data.error || 'Failed to save document', { type: 'error' });
        return false;
      }
      if (typeof data.revision === 'number') revisionRef.current = data.revision;
      savedDocRef.current = stableStringify(snapshot);
      const dirty = stableStringify(docRef.current) !== savedDocRef.current;
      setDocDirty(dirty);
      docDirtyRef.current = dirty;
      toast('Document saved');
      return true;
    } catch (err) {
      console.error('Failed to save document:', err);
      toast('Failed to save document', { type: 'error' });
      return false;
    }
  }, [documentUrl]);

  const saveDocument = useCallback((extras = {}) => {
    const run = saveChainRef.current.then(() => performSave(extras));
    saveChainRef.current = run.catch(() => false);
    return run;
  }, [performSave]);

  const duplicateDocument = useCallback(async () => {
    if (duplicating) return;
    if (!serverDoc?.id) return;
    if (docDirtyRef.current) {
      const saved = await saveDocument();
      if (!saved) return;
    }
    setDuplicating(true);
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
    try {
      const r = await fetch(API.cloneDocument(serverDoc.id), {
        method: 'POST',
        headers: { 'Accept': 'application/json', ...(csrf && { 'X-CSRF-Token': csrf }) }
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast(data.errors?.[0] || data.error || 'Failed to duplicate document', { type: 'error' });
        setDuplicating(false);
        return;
      }
      window.location.href = data.edit_path;
      // No setDuplicating(false) on success — we navigate away.
    } catch (err) {
      console.error('Failed to duplicate document:', err);
      toast('Failed to duplicate document', { type: 'error' });
      setDuplicating(false);
    }
  }, [duplicating, serverDoc, saveDocument, toast]);

  const deleteDocument = useCallback(async () => {
    if (!serverDoc?.id) return;
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
    try {
      const r = await fetch(`${API.document(serverDoc.id)}?notify=1`, {
        method: 'DELETE',
        headers: { 'Accept': 'application/json', ...(csrf && { 'X-CSRF-Token': csrf }) }
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        toast(data.errors?.[0] || data.error || 'Failed to delete document', { type: 'error' });
        return;
      }
      window.track && window.track('document_deleted', { source: 'editor' });
      allowNavRef.current = true;
      window.location.href = '/';
    } catch (err) {
      console.error('Failed to delete document:', err);
      toast('Failed to delete document', { type: 'error' });
    }
  }, [serverDoc, toast]);

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        saveDocument();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [saveDocument]);

  const updatePage = (patch) => {
    setDoc(d => ({ ...d, pages: { ...d.pages, [currentPageId]: { ...d.pages[currentPageId], ...patch } } }));
  };

  const updateTab = useCallback((tabId, patch) => {
    setDoc(d => {
      return { ...d, tabs: d.tabs.map(t => t.id===tabId ? { ...t, ...patch } : t) };
    });
  }, [setDoc]);

  const addTab = () => {
    const id = 't'+Math.random().toString(36).slice(2,7);
    const color = COLORS[doc.tabs.length % COLORS.length];
    const newPageId = 'p'+Math.random().toString(36).slice(2,7);
    setDoc(d => ({
      ...d,
      tabs: [...d.tabs, { id, label:'New tab', color, location:'left', pageIds:[newPageId] }],
      pages: { ...d.pages, [newPageId]: { id:newPageId, tabId:id,
               size:'a4', layout:'single', tabSize:'small', content:null } }
    }));
    setSelectedTabId(id);
    setCurrentPageId(newPageId);
    toast('Tab added');
  };

  const addTabToRail = (loc, railIdx) => {
    const id = 't'+Math.random().toString(36).slice(2,7);
    const newPageId = 'p'+Math.random().toString(36).slice(2,7);
    setDoc(d => ({
      ...d,
      tabs: [...d.tabs, { id, label:'', color:null, location:loc, rail:railIdx, pageIds:[newPageId] }],
      pages: { ...d.pages, [newPageId]: { id:newPageId, tabId:id,
               size:page?.size||'a4', layout:page?.layout||'single', tabSize:page?.tabSize||'small', content:null } }
    }));
    setSelectedTabId(id);
    toast('Tab added · click to name it');
    return id;
  };

  const addInlineTab = () => {
    const id = 't'+Math.random().toString(36).slice(2,7);
    const newPageId = 'p'+Math.random().toString(36).slice(2,7);
    flushEditorGroup();
    flushStructuralGroup();
    pendingInsertSnapshot.current = docRef.current;
    historyRef.current.future = [];
    const next = {
      ...docRef.current,
      tabs: [...docRef.current.tabs, { id, label:'New page', color:null, location:null, inline:true, pageIds:[newPageId] }],
      pages: { ...docRef.current.pages, [newPageId]: { id:newPageId, tabId:id,
               size:page?.size||'a4', layout:page?.layout||'single', tabSize:page?.tabSize||'small', content:null } }
    };
    setDocRaw(next);
    docRef.current = next;
    return id;
  };

  const clonePageButtonSubtree = (oldTabId) => {
    const idMap = new Map();
    const mintTabId = () => 't'+Math.random().toString(36).slice(2,7);
    const mintPageId = () => 'p'+Math.random().toString(36).slice(2,7);

    const queue = [oldTabId];
    const newTabs = [];
    const newPages = {};
    while (queue.length) {
      const oldId = queue.shift();
      if (idMap.has(oldId)) continue;
      const oldTab = docRef.current.tabs.find(t => t.id === oldId);
      if (!oldTab) {
        if (oldId === oldTabId) console.error('clonePageButtonSubtree: root tab not found', oldId);
        continue;
      }
      const newTabId = mintTabId();
      idMap.set(oldId, newTabId);
      const newPageIds = [];
      oldTab.pageIds.forEach(oldPid => {
        const oldPage = docRef.current.pages[oldPid];
        if (!oldPage) return;
        const newPid = mintPageId();
        newPageIds.push(newPid);
        const rewrite = (node) => {
          if (!node) return node;
          if (node.type === 'pageButton' && node.attrs?.tabId) {
            queue.push(node.attrs.tabId);
            return { ...node, attrs: { ...node.attrs, tabId: '__PENDING__:' + node.attrs.tabId } };
          }
          if (node.content) return { ...node, content: node.content.map(rewrite) };
          return node;
        };
        newPages[newPid] = { ...oldPage, id: newPid, tabId: newTabId,
                             content: oldPage.content ? rewrite(oldPage.content) : null,
                             content2: oldPage.content2 ? rewrite(oldPage.content2) : null };
      });
      newTabs.push({ ...oldTab, id: newTabId, pageIds: newPageIds });
    }

    const finalize = (node) => {
      if (!node) return node;
      if (node.type === 'pageButton' && typeof node.attrs?.tabId === 'string' && node.attrs.tabId.startsWith('__PENDING__:')) {
        const oldRef = node.attrs.tabId.slice('__PENDING__:'.length);
        return { ...node, attrs: { ...node.attrs, tabId: idMap.get(oldRef) || oldRef } };
      }
      if (node.content) return { ...node, content: node.content.map(finalize) };
      return node;
    };
    Object.keys(newPages).forEach(pid => {
      newPages[pid].content = finalize(newPages[pid].content);
      newPages[pid].content2 = finalize(newPages[pid].content2);
    });

    if (newTabs.length) {
      // Group the clone with the editor insert that triggered it (paste) so
      // undo collapses them into one step, mirroring addInlineTab's pattern.
      // Skipped on subsequent calls in the same paste batch — we only want
      // the snapshot taken once, against the pre-paste doc.
      if (pendingInsertSnapshot.current === null) {
        flushEditorGroup();
        flushStructuralGroup();
        pendingInsertSnapshot.current = docRef.current;
        historyRef.current.future = [];
      }
      const next = {
        ...docRef.current,
        tabs: [...docRef.current.tabs, ...newTabs],
        pages: { ...docRef.current.pages, ...newPages },
      };
      setDocRaw(next);
      docRef.current = next;
      checkDirty(next);
      bumpHistory();
    }
    return idMap.get(oldTabId);
  };

  const addInlineTabRef = useRef(addInlineTab);
  addInlineTabRef.current = addInlineTab;
  const stableAddInlineTab = useCallback((...args) => addInlineTabRef.current(...args), []);

  const clonePageButtonSubtreeRef = useRef(clonePageButtonSubtree);
  clonePageButtonSubtreeRef.current = clonePageButtonSubtree;
  const stableClonePageButtonSubtree = useCallback((...args) => clonePageButtonSubtreeRef.current(...args), []);

  // Restore an inline subtree previously snapshotted by a cut. Mirrors
  // clonePageButtonSubtree's history grouping so the paste collapses into a
  // single undo step. Existing ids in doc win — protects against double-
  // restore if the user pastes twice within the buffer window.
  const restoreInlineSubtree = ({ tabs, pages }) => {
    if (!tabs || tabs.length === 0) return;
    if (pendingInsertSnapshot.current === null) {
      flushEditorGroup();
      flushStructuralGroup();
      pendingInsertSnapshot.current = docRef.current;
      historyRef.current.future = [];
    }
    const existingIds = new Set(docRef.current.tabs.map(t => t.id));
    const newTabs = tabs.filter(t => !existingIds.has(t.id));
    const next = {
      ...docRef.current,
      tabs: [...docRef.current.tabs, ...newTabs],
      pages: { ...pages, ...docRef.current.pages },
    };
    setDocRaw(next);
    docRef.current = next;
    checkDirty(next);
    bumpHistory();
  };

  const restoreInlineSubtreeRef = useRef(restoreInlineSubtree);
  restoreInlineSubtreeRef.current = restoreInlineSubtree;
  const stableRestoreInlineSubtree = useCallback((...args) => restoreInlineSubtreeRef.current(...args), []);

  const renameInlineTab = useCallback((tabId, label) => updateTab(tabId, { label }), [updateTab]);
  const updateInlineTab = useCallback((tabId, patch) => updateTab(tabId, patch), [updateTab]);

  const collectInlineSubtree = (rootTabId) => {
    const collected = new Set();
    const visit = (tabId) => {
      if (collected.has(tabId)) return;
      collected.add(tabId);
      const tab = doc.tabs.find(t => t.id === tabId);
      if (!tab) return;
      tab.pageIds.forEach(pid => {
        const pg = doc.pages[pid];
        if (!pg?.content) return;
        const walk = (node) => {
          if (!node) return;
          if (node.type === 'pageButton' && node.attrs?.tabId) visit(node.attrs.tabId);
          (node.content || []).forEach(walk);
        };
        walk(pg.content);
      });
    };
    visit(rootTabId);
    collected.delete(rootTabId);
    return [...collected];
  };

  const removePageButton = (content, tabId) => {
    if (!content) return content;
    if (content.type === 'pageButton' && content.attrs?.tabId === tabId) return null;
    if (!content.content) return content;
    const filtered = content.content.map(c => removePageButton(c, tabId)).filter(Boolean);
    return { ...content, content: filtered };
  };

  const requestDeleteInlineTab = (tabId, { silent = false } = {}) => {
    // Only inline tabs are owned by page buttons. Never delete a rail tab
    // because a button pointing at it was removed.
    const inlineIds = new Set(doc.tabs.filter(t => t.inline).map(t => t.id));
    const descendants = collectInlineSubtree(tabId);
    const allTids = new Set([...descendants, tabId].filter(id => inlineIds.has(id)));
    setDocFromEditor(d => {
      const newPages = { ...d.pages };
      if (newPages[currentPageId]?.content) {
        newPages[currentPageId] = {
          ...newPages[currentPageId],
          content: removePageButton(newPages[currentPageId].content, tabId),
        };
      }
      d.tabs.filter(t => allTids.has(t.id)).forEach(t => t.pageIds.forEach(pid => { delete newPages[pid]; }));
      return { ...d, tabs: d.tabs.filter(t => !allTids.has(t.id)), pages: newPages };
    });
    if (!silent) toast('Page button removed', { type: 'info' });
  };

  const requestDeleteInlineTabRef = useRef(requestDeleteInlineTab);
  requestDeleteInlineTabRef.current = requestDeleteInlineTab;
  const stableRequestDeleteInlineTab = useCallback((...args) => requestDeleteInlineTabRef.current(...args), []);

  const removeRail = (loc, railIdx) => {
    const used = doc.tabs.some(t => (t.location||'left')===loc && (t.rail||0)===railIdx);
    if (used) { toast("Rail isn't empty — move or delete its tabs first", { type: 'warning' }); return; }
    setDoc(d => {
      const rails = { ...(d.rails||{}) };
      const cur = rails[loc] || 0;
      rails[loc] = Math.max(0, cur - 1);
      return { ...d, rails };
    });
    toast('Rail removed', { type: 'info' });
  };

  const deleteTab = (tabId) => {
    if (doc.tabs.length <= 1) { toast("Can't delete the last tab", { type: 'warning' }); return; }
    const inlineDescendants = collectInlineSubtree(tabId);
    const allTids = new Set([tabId, ...inlineDescendants]);
    const allDeletedPageIds = new Set();
    doc.tabs.filter(t => allTids.has(t.id)).forEach(t => t.pageIds.forEach(pid => allDeletedPageIds.add(pid)));
    if (allDeletedPageIds.has(currentPageId)) {
      const nextTab = doc.tabs.find(t => !allTids.has(t.id));
      setCurrentPageId(nextTab.pageIds[0]);
    }
    setDoc(d => {
      const newPages = { ...d.pages };
      d.tabs.filter(t => allTids.has(t.id)).forEach(t => t.pageIds.forEach(pid => { delete newPages[pid]; }));
      return { ...d, tabs: d.tabs.filter(t => !allTids.has(t.id)), pages: newPages };
    });
  };


  const handleTabClick = (tabId) => {
    const t = doc.tabs.find(x=>x.id===tabId);
    if (t && t.pageIds.length) {
      setCurrentPageId(t.pageIds[0]);
      setSelectedTabId(tabId);
    }
  };

  const handleTabClickRef = useRef(handleTabClick);
  handleTabClickRef.current = handleTabClick;
  const stableHandleTabClick = useCallback((...args) => handleTabClickRef.current(...args), []);

  const handleTabDoubleClick = (tabIdOrNew) => {
    if (typeof tabIdOrNew === 'string' && tabIdOrNew.startsWith('__new__:')) {
      const loc = tabIdOrNew.split(':')[1];
      const id = 't'+Math.random().toString(36).slice(2,7);
      const color = COLORS[doc.tabs.length % COLORS.length];
      const newPageId = 'p'+Math.random().toString(36).slice(2,7);
      setDoc(d => ({
        ...d,
        tabs: [...d.tabs, { id, label:'New tab', color, location:loc, pageIds:[newPageId] }],
        pages: { ...d.pages, [newPageId]: { id:newPageId, tabId:id,
                 size:'a4', layout:'single', tabSize:'small', content:null } }
      }));
      setSelectedTabId(id);
      toast('Tab added · edit label on the right');
      return;
    }
    setSelectedTabId(tabIdOrNew);
    toast('Edit tab properties on the right', { type: 'info' });
  };

  const handleTabDrop = (targetTabId, targetLoc, targetRail) => {
    if (!dragTabId || dragTabId === targetTabId) { setDragTabId(null); return; }
    setDoc(d => {
      const drag = d.tabs.find(t=>t.id===dragTabId);
      const target = d.tabs.find(t=>t.id===targetTabId);
      if (!drag || !target) return d;
      const dragLoc = drag.location, targetLocVal = target.location;
      const dragRail = drag.rail || 0, targetRailVal = target.rail || 0;
      const a = d.tabs.findIndex(t=>t.id===drag.id);
      const b = d.tabs.findIndex(t=>t.id===target.id);
      const tabs = d.tabs.slice();
      tabs[a] = { ...drag, location: targetLocVal, rail: targetRailVal };
      tabs[b] = { ...target, location: dragLoc, rail: dragRail };
      [tabs[a], tabs[b]] = [tabs[b], tabs[a]];
      return { ...d, tabs };
    });
    setDragTabId(null);
    toast('Tabs swapped');
  };

  const handleExport = async () => {
    setShowExport(false);
    setExporting(true);
    try {
      if (docDirtyRef.current) {
        const saved = await saveDocument();
        if (!saved) return;
      }
      await exportPdf({ id: serverDoc.id, name: doc.name }, { onToast: toast });
      window.track && window.track('pdf_exported', { source: 'editor' });
    } finally {
      setExporting(false);
    }
  };

  const isMinimized = uiMinimized || responsiveMode;
  const editorClass = `editor${isMinimized ? ' ui-minimized' : ''}${responsiveMode ? ' responsive-mode' : ''} ${doc.orientation === 'landscape' ? 'landscape' : 'portrait'}`;

  const bbFieldsEl = document.getElementById('editor-bb-fields');
  const bbActionsEl = document.getElementById('editor-bb-actions');

  return (
    <>
      {!readOnly && bbFieldsEl && createPortal(
        <div className="bb-doc-fields">
          <a href="/" className="bb-back" title="Back to library" onClick={(e)=>{if(docDirty){e.preventDefault();pendingNavRef.current='/';setShowUnsaved(true);}}}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </a>
          <span className="bb-sep"/>
          <div className="bb-title-wrap">
            <button className="bb-title-pill" aria-expanded={showDocInfo} onClick={() => setShowDocInfo(o => !o)}>
              <span className="bb-title-text">{doc.name || 'Untitled document'}</span>
              <svg className="bb-title-caret" width="12" height="12" viewBox="0 0 16 16" fill="none" style={showDocInfo ? {transform:'rotate(180deg)'} : undefined}><path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5"/></svg>
              <svg className="bb-title-edit" width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M11.5 2.5l2 2M2 11l7.5-7.5 2 2L4 13H2v-2z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
            {showDocInfo && (
              <>
                <div className="di-backdrop" onClick={() => setShowDocInfo(false)}/>
                <DocInfoPopover
                  doc={doc}
                  tags={doc.tags || []}
                  onUpdateDoc={patch => setDocMeta(d => ({ ...d, ...patch }))}
                  onUpdateTags={tags => setDocMeta(d => ({ ...d, tags }))}
                  onClose={() => setShowDocInfo(false)}/>
              </>
            )}
          </div>
          {(doc.tags || []).slice(0, 4).map((tag, i) => (
            <span key={i} className={`chip chip--tag`}>{tag}</span>
          ))}
          {(doc.tags || []).length > 4 && <span className="chip chip--muted">+{doc.tags.length - 4}</span>}
        </div>,
        bbFieldsEl
      )}

      {!readOnly && bbActionsEl && createPortal(
        <>
          {docDirty && (
            <>
              <span className="bb-unsaved-label">Unsaved changes</span>
              <button className="btn btn--tertiary btn--md bb-save-btn" onClick={()=>{saveDocument();}}>Save</button>
            </>
          )}
          {mode !== 'preview' ? (
            <button className="btn btn--secondary btn--md bb-action-btn" onClick={() => setMode('preview')} title="Preview">
              <Ico.Eye width="14" height="14"/><span className="bb-btn-label"> Preview</span>
            </button>
          ) : (
            <button className="btn btn--secondary btn--md bb-action-btn" onClick={() => setMode('edit')} title="Back to editing">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              <span className="bb-btn-label"> Back to editing</span>
            </button>
          )}
          <div className={`btn-combo bb-combo${showDocMenu ? ' open' : ''}`}>
            <button className="btn btn--primary btn--md bb-action-btn" onClick={()=>{
              if (!exportable) { setShowPublish(true); return; }
              if (docDirty) setShowExport(true); else handleExport();
            }} disabled={exporting} title="Export PDF">
              <Ico.Download width="14" height="14"/><span className="bb-btn-label">{exporting ? ' Exporting…' : ' Export PDF'}</span>
            </button>
            <button className="btn btn--primary btn--md bb-combo-trigger"
                    aria-label="More document actions"
                    aria-haspopup="menu"
                    aria-expanded={showDocMenu}
                    onClick={()=>setShowDocMenu(m=>!m)}>
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" style={showDocMenu ? {transform:'rotate(180deg)'} : undefined}><path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>
            </button>
            {showDocMenu && (
              <>
                <div className="bb-combo-backdrop" onClick={()=>setShowDocMenu(false)}/>
                <div className="menu bb-combo-menu">
                  <button className="menu__item" disabled={duplicating}
                          onClick={()=>{setShowDocMenu(false);duplicateDocument();}}>
                    <Ico.Copy width="14" height="14"/> {duplicating ? 'Duplicating…' : 'Duplicate document'}
                  </button>
                  <div className="menu__separator"/>
                  <button className="menu__item menu__item--danger" onClick={()=>{setShowDocMenu(false);setShowDeleteDoc(true);}}>
                    <Ico.Trash width="14" height="14"/> Delete document
                  </button>
                </div>
              </>
            )}
          </div>
        </>,
        bbActionsEl
      )}

      <div className={`editor-shell${mode==='preview' ? ' preview-mode' : ''}`}>
        <div className={editorClass}>
          {mode !== 'preview' && !responsiveMode && (
            <PageList doc={doc} currentPageId={currentPageId}
                      onSelect={setCurrentPageId}
                      collapsed={isMinimized}
                      onToggle={() => setUiMinimized(c => !c)}/>
          )}
          <div className="workspace-wrap">
            {!readOnly && <TiptapToolbar
              editor={activeEditor}
              zoom={zoom}
              onSetZoom={setZoom}
              preview={mode === 'preview'}
              onUndo={undo}
              onRedo={redo}
              canUndo={canUndo}
              canRedo={canRedo}
              responsiveMode={responsiveMode}
              onTogglePages={() => setDrawerOpen(d => d === 'pages' ? null : 'pages')}
              onToggleSettings={() => setDrawerOpen(d => d === 'settings' ? null : 'settings')}
            />}
            <div className="workspace" ref={workspaceRef}>
              <div className="zoom-layer" style={{zoom: (zoom === 'fit' ? fitZoom : zoom) / 100}}>
                {orderedTabs.flatMap(t => t.pageIds.map(pid => {
                  const pg = doc.pages[pid];
                  if (!pg) return null;
                  const isCurrent = pid === currentPageId;
                  return (
                    <div key={pid} id={`page-${pid}`} className={`print-page ${isCurrent ? '' : 'screen-hidden'}`}>
                      <Page doc={doc} page={pg}
                            documentId={serverDoc?.id}
                            currentTabId={pg.tabId}
                            onTabClick={handleTabClick}
                            onTabDoubleClick={handleTabDoubleClick}
                            onTabDrop={handleTabDrop}
                            dragTabId={dragTabId}
                            setDragTabId={setDragTabId}
                            isEditing={isCurrent && mode==='edit'}
                            onEditContent={(c)=>{ if(isCurrent) setDocFromEditor(d=>({...d, pages:{...d.pages, [pid]:{...d.pages[pid], content:c}}})); }}
                            onEditContent2={(c)=>{ if(isCurrent) setDocFromEditor(d=>({...d, pages:{...d.pages, [pid]:{...d.pages[pid], content2:c}}})); }}
                            onEditImage={(imgs)=>{
                              setDoc(d=>{
                                const pg = d.pages[pid];
                                return {...d, pages:{...d.pages, [pid]:{...pg, images:imgs}}};
                              });
                            }}
                            onUpdateTab={updateTab}
                            onDeleteTab={deleteTab}
                            onAddTabToRail={addTabToRail}
                            onSetRailCount={(loc, n)=>setDoc(d=>({...d, rails:{...(d.rails||{}), [loc]: Math.max(0, n)}}))}
                            onRemoveRail={removeRail}
                            onUpdatePage={isCurrent ? updatePage : undefined}
                            onEditorReady={handleEditorReady}
                            onInsertInlineTab={stableAddInlineTab}
                            onOpenPage={stableHandleTabClick}
                            onRenameInlineTab={renameInlineTab}
                            onUpdateInlineTab={updateInlineTab}
                            onDeleteInlineTab={stableRequestDeleteInlineTab}
                            onClonePageButtonSubtree={stableClonePageButtonSubtree}
                            onRestoreInlineSubtree={stableRestoreInlineSubtree}/>
                    </div>
                  );
                }))}
              </div>
            </div>
          </div>

          {mode !== 'preview' && page && !isMinimized && !responsiveMode && (
            <StructureSettings
              doc={doc} page={page}
              onUpdateDoc={(patch)=>setDoc(d=>({...d, ...patch}))}
              onUpdatePage={updatePage}
              onSetRailSize={(loc, size)=>setDoc(d=>({...d, railSizes: {...(d.railSizes||{}), [loc]: size}}))}
            />
          )}

          {responsiveMode && mode !== 'preview' && (
            <>
              {drawerOpen && <div className="drawer-backdrop" onClick={() => setDrawerOpen(null)}/>}
              <div className={`drawer drawer--left${drawerOpen === 'pages' ? ' drawer--open' : ''}`}>
                <div className="drawer__header">
                  <span className="drawer__title">Pages</span>
                  <button className="tt-btn" onClick={() => setDrawerOpen(null)} title="Close" aria-label="Close">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                  </button>
                </div>
                <PageList doc={doc} currentPageId={currentPageId}
                          onSelect={(pid) => { setCurrentPageId(pid); setDrawerOpen(null); }}
                          collapsed={false}
                          onToggle={() => setDrawerOpen(null)}/>
              </div>
              <div className={`drawer drawer--right${drawerOpen === 'settings' ? ' drawer--open' : ''}`}>
                <div className="drawer__header">
                  <span className="drawer__title">Settings</span>
                  <button className="tt-btn" onClick={() => setDrawerOpen(null)} title="Close" aria-label="Close">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                  </button>
                </div>
                {page && (
                  <StructureSettings
                    doc={doc} page={page}
                    onUpdateDoc={(patch)=>setDoc(d=>({...d, ...patch}))}
                    onUpdatePage={updatePage}
                    onSetRailSize={(loc, size)=>setDoc(d=>({...d, railSizes: {...(d.railSizes||{}), [loc]: size}}))}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {!readOnly && showPublish && (
        <PublishModal docName={doc.name} onClose={()=>setShowPublish(false)} onConfirm={async ()=>{
          setShowPublish(false);
          const saved = await saveDocument({ visibility: 'public' });
          if (!saved) return;
          setExportable(true);
          window.track && window.track('document_published', { source: 'editor' });
          handleExport();
        }}/>
      )}
      {!readOnly && showExport && <ExportModal doc={doc} onClose={()=>setShowExport(false)} onExport={handleExport}/>}
      {!readOnly && showDeleteDoc && (
        <DeleteDocModal docName={doc.name} onClose={()=>setShowDeleteDoc(false)} onConfirm={()=>{setShowDeleteDoc(false);deleteDocument();}}/>
      )}
      {!readOnly && conflict && <ConflictModal
        onReload={()=>{allowNavRef.current=true;window.location.reload();}}
        onSaveMine={()=>{const c=conflict;setConflict(null);if(typeof c.revision==='number')revisionRef.current=c.revision;saveDocument();}}
        onClose={()=>setConflict(null)}
      />}
      {!readOnly && showUnsaved && <UnsavedModal
        onSave={async ()=>{setShowUnsaved(false);const nav=pendingNavRef.current;pendingNavRef.current=null;const ok=await saveDocument();if(ok){allowNavRef.current=true;if(nav instanceof HTMLFormElement)nav.submit();else window.location.href=nav;}}}
        onDiscard={()=>{setShowUnsaved(false);const nav=pendingNavRef.current;pendingNavRef.current=null;allowNavRef.current=true;if(nav instanceof HTMLFormElement)nav.submit();else window.location.href=nav;}}
        onClose={()=>{setShowUnsaved(false);pendingNavRef.current=null;}}
      />}
    </>
  );
}
